import React, { forwardRef, ChangeEvent, KeyboardEvent, useRef } from 'react'
import clsx from 'clsx'
import { useCombinedRef } from '../useCombinedRef'
import { useThemeClass } from '../Theme'
import { ALLOWED_KEYS, EMPTY_DISPLAY, VARIANT } from './constants'
import styles from './style.module.scss'
import { noop } from '../utils/function'
import { SupportedLocale } from '@afterpay/types'
import { isCallable, isUndefinedOrEmpty } from '@afterpay/utils'
import { getDisplayValue, getInputSize } from './helper'
import { useIsMobileBrowser } from '../useDevice'
import { EmptyDisplay, Variant } from './types'

export interface InputMoneyProps extends Omit<React.HTMLAttributes<HTMLInputElement>, 'onChange' | 'size'> {
  currencySymbol?: string
  value?: string
  onChange?: (value: string, event: ChangeEvent<HTMLInputElement>) => void
  onBlur?: () => void
  disabled?: boolean
  placeholder?: string
  readOnly?: boolean
  required?: boolean
  tabIndex?: number
  testNameSpace?: string
  maxValue?: number
  maxDigits?: number
  bold?: boolean
  locale?: SupportedLocale
  autoFocus?: boolean
  emptyDisplay?: EmptyDisplay
  variant?: Variant
  'aria-label'?: string
  'aria-describedby'?: string
  'aria-disabled'?: boolean
  'data-dd-action-name'?: string
}

export const InputMoney = forwardRef<HTMLInputElement, InputMoneyProps>(
  (
    {
      currencySymbol = '$',
      value = '',
      onChange = noop,
      onBlur = noop,
      disabled = false,
      placeholder,
      readOnly = false,
      required = false,
      tabIndex = 0,
      testNameSpace = '',
      maxValue,
      maxDigits,
      bold = false,
      locale,
      className,
      autoFocus = false,
      emptyDisplay = EMPTY_DISPLAY.ZERO,
      variant,
      'aria-label': ariaLabel,
      'aria-describedby': ariaDescribedBy,
      'aria-disabled': ariaDisabled,
      'data-dd-action-name': dataDdActionName,
      ...otherProps
    },
    ref
  ) => {
    const themeClass = useThemeClass()
    const innerRef = useRef<HTMLInputElement>(null)
    const combinedRef = useCombinedRef(ref, innerRef)
    const isMobileBrowser = useIsMobileBrowser()

    const hasTestId = testNameSpace.length > 0
    const inputTabIndex = disabled ? -1 : tabIndex

    const effectiveValue = isUndefinedOrEmpty(value) && emptyDisplay === EMPTY_DISPLAY.ZERO ? '0' : value
    const displayValue = getDisplayValue(effectiveValue, locale)
    const inputSize = getInputSize(displayValue, placeholder, isMobileBrowser)

    const componentClassNames = clsx(
      styles.component,
      disabled && styles.modDisabled,
      readOnly && styles.modReadOnly,
      bold && styles.modBold,
      styles[themeClass],
      className
    )
    const inputClassNames = clsx(
      styles.input,
      bold && styles.modBold,
      isUndefinedOrEmpty(value) && styles.modEmpty,
      variant === VARIANT.SUCCESS && styles.modSuccess,
      variant === VARIANT.ERROR && styles.modError,
      styles[themeClass]
    )
    const currencyClassNames = clsx(
      styles.currency,
      bold && styles.modBold,
      variant === VARIANT.SUCCESS && styles.modSuccess,
      variant === VARIANT.ERROR && styles.modError,
      styles[themeClass]
    )

    const sanitizeValue = (val: string): string => {
      let sanitized = val.replace(/[^0-9.]/g, '')

      // If there's more than one decimal point, remove the extra decimal point
      const parts = sanitized.split('.')
      if (parts.length > 2) {
        sanitized = parts[0] + '.' + parts.slice(1).join('')
      }

      // Limit decimal places to 2 digits
      if (parts.length === 2 && parts[1].length > 2) {
        sanitized = parts[0] + '.' + parts[1].slice(0, 2)
      }

      return sanitized
    }

    const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
      const newValue = event.target.value
      let sanitizedValue = sanitizeValue(newValue)

      // Remove leading zeros, unless the value is a decimal point
      if (sanitizedValue !== '' && sanitizedValue !== '.' && !sanitizedValue.startsWith('0.')) {
        sanitizedValue = sanitizedValue.replace(/^0+/, '') || '0'
      }

      // If maxDigits is set, limit based on integer digit count
      if (typeof maxDigits !== 'undefined' && sanitizedValue !== '' && sanitizedValue !== '.') {
        const integerPart = sanitizedValue.split('.')[0]

        if (integerPart !== '0' && integerPart !== '0.') {
          const withoutLeadingZeros = integerPart.replace(/^0+/, '')
          const digitCount = withoutLeadingZeros.length

          if (digitCount > maxDigits) {
            return
          }
        }
      }

      // If a max value is set, limit the value to the max value
      if (typeof maxValue !== 'undefined' && sanitizedValue !== '' && sanitizedValue !== '.') {
        const numericValue = parseFloat(sanitizedValue)
        if (!isNaN(numericValue) && numericValue > maxValue) {
          sanitizedValue = maxValue.toString()
        }
      }

      if (newValue !== sanitizedValue) {
        event.target.value = sanitizedValue
      }

      if (isCallable(onChange)) {
        onChange(sanitizedValue, event)
      }
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
      const isNumber = /^[0-9]$/.test(event.key)
      const isAllowedKey = ALLOWED_KEYS.includes(event.key)

      // Allow decimal point only if there isn't one already
      if (event.key === '.') {
        if (value.includes('.')) {
          event.preventDefault()
        }
        return
      }

      // If it's not a number and not an allowed key, prevent it
      if (!isAllowedKey && !isNumber) {
        event.preventDefault()
      }
    }

    // Handle case where user ends with a decimal point, and then call onBlur
    const handleBlur = (): void => {
      if (value.endsWith('.')) {
        const cleanedValue = value.slice(0, -1)
        if (innerRef.current != null) {
          innerRef.current.value = cleanedValue
          // Dispatch a real change event
          const event = new Event('change', { bubbles: true })
          innerRef.current.dispatchEvent(event)
        }
      }

      if (isCallable(onBlur)) {
        onBlur()
      }
    }

    return (
      <div className={componentClassNames} {...(hasTestId && { 'data-testid': `${testNameSpace}-wrapper` })}>
        <span className={currencyClassNames} {...(hasTestId && { 'data-testid': `${testNameSpace}-currency` })} aria-hidden='true'>
          {currencySymbol}
        </span>
        <input
          ref={combinedRef}
          type='text'
          inputMode='decimal'
          autoComplete='off'
          autoCorrect='off'
          spellCheck='false'
          value={displayValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          autoFocus={autoFocus}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          tabIndex={inputTabIndex}
          placeholder={placeholder}
          size={inputSize}
          className={inputClassNames}
          aria-label={ariaLabel}
          aria-describedby={ariaDescribedBy}
          aria-disabled={disabled ?? ariaDisabled}
          data-dd-action-name={dataDdActionName ?? `${testNameSpace} Money Input`}
          {...(hasTestId && { 'data-testid': `${testNameSpace}-money-input` })}
          {...otherProps}
        />
      </div>
    )
  }
)

InputMoney.displayName = 'InputMoney'
